import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import {
  Connection,
  ConnectionEvent,
  Provider,
  SecretIntake,
  CreateConnectionBody,
  TestResult,
  IntrospectionRun,
  DiscoveryOptions,
  listConnections as apiList,
  listProviders as apiProviders,
  createConnection as apiCreate,
  updateConnection as apiUpdate,
  deleteConnection as apiDelete,
  setDefaultConnection as apiSetDefault,
  testConnection as apiTest,
  listConnectionEvents as apiEvents,
  storeSecret as apiStoreSecret,
  startIntrospection as apiStartIntrospection,
  getIntrospectionRun as apiGetIntrospectionRun,
  listIntrospectionRuns as apiListIntrospectionRuns,
} from '../../components/Connections/api';

// Poll timers for in-flight introspection runs, keyed by connection id.
// Module scope, not store state: timer handles aren't renderable data.
const introspectionTimers: Record<string, ReturnType<typeof setTimeout>> = {};
// Connections whose start POST is still in flight — a synchronous lock, so a
// double-click cannot observe "no active run" twice and start two runs.
const introspectionStarting = new Set<string>();
const INTROSPECTION_POLL_MS = 2500;
// Transient poll failures tolerated before we stop and mark the run lost.
const INTROSPECTION_MAX_POLL_FAILURES = 8;

const isRunActive = (run?: { status: string }) =>
  !!run && (run.status === 'pending' || run.status === 'running');

export interface ConnectionsSlice {
  connections: Connection[];
  providers: Provider[];
  /** Which credential flows this deployment supports; drives the auth step. */
  secretIntake: SecretIntake;
  connectionsLoading: boolean;
  connectionsError: string | null;
  /** id -> true while a connectivity test is in flight, so rows spin individually. */
  connectionTesting: Record<string, boolean>;
  connectionEvents: Record<string, ConnectionEvent[]>;
  selectedConnectionId: string | null;
  /**
   * connection id -> most recent introspection run we know about. Populated
   * when a run is started from this session and refreshed by polling; also
   * lazily filled from the runs API so completed runs survive a reload.
   */
  introspectionRuns: Record<string, IntrospectionRun>;
  /** Bumped on every identity/workspace switch; a fetch that outlived one is dropped. */
  connectionsGeneration: number;

  /**
   * The workspace changed: forget every connection, ledger, run and the
   * open drawer — they belong to the one we left. Re-read when the
   * Connections page is on screen.
   */
  resetConnectionsWorkspace: () => void;
  fetchConnections: () => Promise<void>;
  fetchProviders: () => Promise<void>;
  storeSecret: (label: string, value: string) => Promise<string>;
  createConnection: (body: CreateConnectionBody) => Promise<Connection>;
  updateConnection: (id: string, updates: Partial<CreateConnectionBody>) => Promise<Connection>;
  deleteConnection: (id: string) => Promise<void>;
  setDefaultConnection: (id: string) => Promise<void>;
  testConnection: (id: string) => Promise<TestResult>;
  fetchConnectionEvents: (id: string) => Promise<void>;
  selectConnection: (id: string | null) => void;
  /** Kick off a cold-start run and poll it to a terminal state. */
  introspectConnection: (id: string, options?: DiscoveryOptions) => Promise<void>;
  /** Load the latest known run for a connection (e.g. on detail-panel open). */
  /** Resolves false when the history could not be read (callers that show it must not read that as "no runs"). */
  fetchLatestIntrospectionRun: (id: string) => Promise<boolean>;
}

export const createConnectionsSlice: StateCreator<
  VoyagerState, [], [], ConnectionsSlice
> = (set, get) => {
  /**
   * Poll one run to a terminal state, writing progress into the store.
   * Owns the single timer slot per connection: scheduling for a new run
   * cancels any poll loop a previous run left behind.
   */
  const pollIntrospectionRun = (connectionId: string, runId: string) => {
    let failures = 0;
    // The workspace the run belongs to. A tick that fires, or resolves, after
    // a switch writes nothing and schedules no further tick.
    const gen = get().connectionsGeneration;
    const stale = () => get().connectionsGeneration !== gen;

    const stop = () => {
      delete introspectionTimers[connectionId];
    };

    const poll = async () => {
      delete introspectionTimers[connectionId];
      if (stale()) return;
      // The connection may have been deleted while polling; its runs cascade
      // away with it, so there is nothing left to ask about.
      if (!get().connections.some((c) => c.id === connectionId)) return;

      let latest: IntrospectionRun;
      try {
        latest = await apiGetIntrospectionRun(connectionId, runId);
        if (stale()) return;
        failures = 0;
      } catch (e: any) {
        if (stale()) return;
        const status = e?.status ?? 0;
        const permanent = status === 404 || status === 401 || status === 403;
        failures += 1;
        if (permanent || failures >= INTROSPECTION_MAX_POLL_FAILURES) {
          // Give up loudly rather than leak a timer that retries forever.
          const current = get().introspectionRuns[connectionId];
          if (current && current.id === runId && isRunActive(current)) {
            set({
              introspectionRuns: {
                ...get().introspectionRuns,
                [connectionId]: {
                  ...current, status: 'failed',
                  error: permanent
                    ? 'The run is no longer reachable (connection removed or access revoked).'
                    : 'Lost contact with the run — reload the page to check its state.',
                },
              },
            });
          }
          stop();
          return;
        }
        introspectionTimers[connectionId] = setTimeout(poll, INTROSPECTION_POLL_MS * 2);
        return;
      }

      // A newer run may own the slot by now (re-introspect while we slept);
      // never let a stale run's snapshot overwrite it.
      const current = get().introspectionRuns[connectionId];
      if (current && current.id && current.id !== runId && current.created_at > latest.created_at) {
        return;
      }
      set({ introspectionRuns: { ...get().introspectionRuns, [connectionId]: latest } });
      if (isRunActive(latest)) {
        introspectionTimers[connectionId] = setTimeout(poll, INTROSPECTION_POLL_MS);
        return;
      }
      // Terminal: the run wrote an audit event; keep an open detail panel honest.
      if (get().connectionEvents[connectionId]) {
        get().fetchConnectionEvents(connectionId);
      }
  };

    // Cancel whatever loop the previous run left in this connection's slot.
    if (introspectionTimers[connectionId]) clearTimeout(introspectionTimers[connectionId]);
    introspectionTimers[connectionId] = setTimeout(poll, INTROSPECTION_POLL_MS);
  };

  return {
  connections: [],
  providers: [],
  secretIntake: { vault_write: false, backend: 'none', env_prefixes: [] },
  connectionsLoading: false,
  connectionsError: null,
  connectionTesting: {},
  connectionEvents: {},
  selectedConnectionId: null,
  introspectionRuns: {},
  connectionsGeneration: 0,

  resetConnectionsWorkspace: () => {
    const onConnections = get().currentView === 'connections';
    // Pending poll ticks belong to the workspace we are leaving.
    Object.keys(introspectionTimers).forEach((id) => { clearTimeout(introspectionTimers[id]); delete introspectionTimers[id]; });
    set({
      connections: [], connectionsLoading: false, connectionsError: null, connectionTesting: {}, connectionEvents: {},
      selectedConnectionId: null, introspectionRuns: {},
      connectionsGeneration: get().connectionsGeneration + 1,
    });
    if (onConnections) get().fetchConnections().catch(() => {});
  },

  fetchConnections: async () => {
    const gen = get().connectionsGeneration;
    set({ connectionsLoading: true, connectionsError: null });
    try {
      const list = await apiList();
      if (get().connectionsGeneration !== gen) return;
      set({ connections: list, connectionsLoading: false });
    } catch (e: any) {
      if (get().connectionsGeneration !== gen) return;
      set({ connectionsError: e?.message || 'Could not load connections', connectionsLoading: false });
    }
  },

  fetchProviders: async () => {
    // The catalogue is static for the life of the process; fetch it once.
    if (get().providers.length) return;
    try {
      const { providers, secretIntake } = await apiProviders();
      set({ providers, secretIntake });
    } catch (e: any) {
      set({ connectionsError: e?.message || 'Could not load providers' });
    }
  },

  /**
   * Send a credential to the backend for storage, get back a reference.
   * Nothing here retains the value — it is passed straight through.
   */
  storeSecret: async (label, value) => apiStoreSecret(label, value),

  // Every write below captures the workspace generation before its request and
  // applies the result only if no identity/org switch happened meanwhile: a row
  // made, changed or tested in the workspace we left must never land in this
  // one. The result is still returned, so the caller's own flow is unaffected.
  createConnection: async (body) => {
    const gen = get().connectionsGeneration;
    const created = await apiCreate(body);
    if (get().connectionsGeneration === gen) set({ connections: [created, ...get().connections] });
    return created;
  },

  updateConnection: async (id, updates) => {
    const gen = get().connectionsGeneration;
    const updated = await apiUpdate(id, updates);
    if (get().connectionsGeneration === gen) set({ connections: get().connections.map((c) => (c.id === id ? updated : c)) });
    return updated;
  },

  deleteConnection: async (id) => {
    const gen = get().connectionsGeneration;
    await apiDelete(id);
    if (get().connectionsGeneration !== gen) return;
    set({
      connections: get().connections.filter((c) => c.id !== id),
      selectedConnectionId: get().selectedConnectionId === id ? null : get().selectedConnectionId,
    });
  },

  setDefaultConnection: async (id) => {
    const gen = get().connectionsGeneration;
    const updated = await apiSetDefault(id);
    if (get().connectionsGeneration !== gen) return;
    // Exactly one default per owner — clear the others locally rather than refetch.
    set({
      connections: get().connections.map((c) =>
        c.id === id ? updated : { ...c, is_default: false },
      ),
    });
  },

  testConnection: async (id) => {
    const gen = get().connectionsGeneration;
    set({ connectionTesting: { ...get().connectionTesting, [id]: true } });
    try {
      const result = await apiTest(id);
      if (get().connectionsGeneration !== gen) return result;
      set((state) => ({
        connections: state.connections.map((c) =>
          c.id === id
            ? (result.connection ?? {
                ...c,
                status: result.status,
                last_error: result.error || '',
                last_tested_at: new Date().toISOString(),
              })
            : c,
        ),
      }));
      // The test wrote an audit event; keep an open detail panel honest.
      if (get().connectionEvents[id]) get().fetchConnectionEvents(id);
      return result;
    } finally {
      const testing = { ...get().connectionTesting };
      delete testing[id];
      set({ connectionTesting: testing });
    }
  },

  fetchConnectionEvents: async (id) => {
    const gen = get().connectionsGeneration;
    try {
      const events = await apiEvents(id);
      if (get().connectionsGeneration !== gen) return;
      set({ connectionEvents: { ...get().connectionEvents, [id]: events } });
    } catch {
      if (get().connectionsGeneration !== gen) return;
      set({ connectionEvents: { ...get().connectionEvents, [id]: [] } });
    }
  },

  selectConnection: (id) => set({ selectedConnectionId: id }),

  introspectConnection: async (id, options) => {
    // One run per connection at a time. The Set is checked-and-added
    // synchronously, so a double-click cannot start two backend runs while
    // the first POST is still in flight.
    if (introspectionStarting.has(id) || isRunActive(get().introspectionRuns[id])) return;
    introspectionStarting.add(id);
    // A start that resolves after a workspace switch neither records its run
    // nor polls it here — the run is the workspace we left's.
    const gen = get().connectionsGeneration;
    const stale = () => get().connectionsGeneration !== gen;
    try {
      // Local state can lag reality: right after a reload, hydration may not
      // have landed yet and the backend does not reject concurrent runs. Ask
      // the server inside the lock, and ADOPT an active run instead of
      // starting a duplicate that would race the same draft.
      try {
        const existing = (await apiListIntrospectionRuns(id, 1))[0];
        if (stale()) return;
        if (existing && isRunActive(existing)) {
          set({ introspectionRuns: { ...get().introspectionRuns, [id]: existing } });
          pollIntrospectionRun(id, existing.id);
          return;
        }
      } catch {
        // If the pre-check itself fails, the POST below will fail the same
        // way and surface through the failed-chip path.
      }
      const run = await apiStartIntrospection(id, options);
      if (stale()) return;
      set({ introspectionRuns: { ...get().introspectionRuns, [id]: run } });
      pollIntrospectionRun(id, run.id);
    } catch (e: any) {
      if (stale()) return;
      // Surface the refusal on the row instead of an unhandled rejection —
      // a synthetic failed run renders through the same chip as a real one.
      set({
        introspectionRuns: {
          ...get().introspectionRuns,
          [id]: {
            id: '', connection_id: id, status: 'failed', stage: '',
            error: e?.message || 'Could not start introspection',
            result: {}, draft_id: null, table_count: 0, column_count: 0,
            created_at: new Date().toISOString(), started_at: null, completed_at: null,
          },
        },
      });
    } finally {
      introspectionStarting.delete(id);
    }
  },

  fetchLatestIntrospectionRun: async (id) => {
    // Don't clobber an actively polled run with a list snapshot.
    if (isRunActive(get().introspectionRuns[id])) return true;
    const gen = get().connectionsGeneration;
    try {
      const runs = await apiListIntrospectionRuns(id, 1);
      if (!runs.length || get().connectionsGeneration !== gen) return true;
      set({ introspectionRuns: { ...get().introspectionRuns, [id]: runs[0] } });
      // A run that was in flight when the page loaded must resume polling,
      // or the row spins forever and the draft only appears after a reload.
      if (isRunActive(runs[0])) pollIntrospectionRun(id, runs[0].id);
      return true;
    } catch {
      // Absence of history is not an error state for the page.
      return false;
    }
  },
  };
};
